import { useAtom } from "@dbeining/react-atom";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useMemo, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { userAtom } from "../../atoms";
import { ConfirmationModel } from "../../components/features/common";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { ColumnRender, FilterType, IColumn, IFilter, IPagedListAction, SortOrder } from "../../components/UI/Declarations";
import { PagedList } from "../../components/UI/PagedList/PagedList";
import { IRole } from "../../customTypings/user/Role";
import { sitesService } from "../../services";
import type { IPostalAddress } from "../../customTypings/address/PostalAddress";
import { addEntityActionLabel, buildRecordLifecycleConfirmProps, EntityLabels, FilterMigration, formatPostalAddress, ListPageTitles, recordLifecycleListConfirmBodies, Role, SectionLabels, showToastSuccess } from "../../utils";
import {
    buildSiteAdminPagedListActions,
    getSiteAdminActionSuccessMessage,
    type SiteAdminConfirmKind,
} from "./siteAdminListActions";

type ConfirmKind = SiteAdminConfirmKind | null;

const sitesListFilterMigrations: Record<string, FilterMigration> = {
    includeDeleted: {
        member: "isDeleted",
        mapValue: (value) => (value === true ? null : false),
    },
    postcodeNormalizedPrefix: {
        member: "addressSearch",
        mapValue: (value) => (typeof value === "string" && value.trim() ? value : null),
    },
};

const SitesListPage = () => {
    const navigate = useNavigate();
    const currentUser = useAtom(userAtom);
    const isAdmin = currentUser?.identity.roles.some(
        (role: IRole) => role.name === Role.Admin || role.name === Role.SystemAdmin
    );

    const [refreshNumber, setRefreshNumber] = useState(0);
    const [actionError, setActionError] = useState<string | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [confirmSiteId, setConfirmSiteId] = useState<string | null>(null);
    const [confirmSiteLabel, setConfirmSiteLabel] = useState("");

    const closeConfirm = () => {
        setConfirmKind(null);
        setConfirmSiteId(null);
        setConfirmSiteLabel("");
    };

    const runConfirmedAction = () => {
        if (!confirmSiteId || !confirmKind) return;
        const id = confirmSiteId;
        const op =
            confirmKind === "disable"
                ? sitesService.disable(id)
                : confirmKind === "enable"
                  ? sitesService.enable(id)
                  : confirmKind === "softDelete"
                    ? sitesService.softDelete(id)
                    : sitesService.restore(id);
        op.then(() => {
            showToastSuccess(getSiteAdminActionSuccessMessage(confirmKind, confirmSiteLabel));
            setActionError(null);
            setRefreshNumber((n) => n + 1);
            closeConfirm();
        }).catch((e: unknown) => {
            setActionError(typeof e === "string" ? e : "That action couldn't be completed. Please try again.");
            closeConfirm();
        });
    };

    const openConfirm = (kind: SiteAdminConfirmKind, item: { id: string; siteName: string }) => {
        setConfirmKind(kind);
        setConfirmSiteId(item.id);
        setConfirmSiteLabel(item.siteName);
    };

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.site,
                confirmSiteLabel,
                {
                    ...recordLifecycleListConfirmBodies(confirmSiteLabel),
                    disable: `Disable "${confirmSiteLabel}"? Users will not be able to select this site until it is enabled again.`,
                    enable: `Enable "${confirmSiteLabel}" for use again?`,
                    restore: `Restore "${confirmSiteLabel}"? It will reappear in lists (still disabled until you enable it).`,
                }
            ),
        [confirmKind, confirmSiteLabel]
    );

    const columns: IColumn[] = useMemo(
        () => [
            { member: "siteName", renderAs: ColumnRender.text, title: "Name", width: 200 } as IColumn,
            {
                member: "address",
                renderAs: ColumnRender.customRenderer,
                title: "Address",
                width: 260,
                customRenderer: (item: IPostalAddress) => (
                    <td className="text-start text-muted small">{formatPostalAddress(item) || "-"}</td>
                ),
            } as IColumn,
            { member: "environmentalPermitNumber", renderAs: ColumnRender.text, title: "Permit", width: 140 } as IColumn,
            { member: "facilityOperationCode", renderAs: ColumnRender.text, title: "Facility code", width: 120 } as IColumn,
            { member: "weighbridgeUsedFlag", renderAs: ColumnRender.checkCross, title: "Weighbridge", width: 110, align: "center" } as IColumn,
            { member: "reportingActiveFlag", renderAs: ColumnRender.checkCross, title: "Reporting", width: 100, align: "center" } as IColumn,
            { member: "isEnabled", renderAs: ColumnRender.checkCross, title: "Enabled", width: 90, align: "center" } as IColumn,
        ],
        []
    );

    const filters: IFilter[] = useMemo(
        () => [
            { title: "Name", member: "siteName", type: FilterType.text } as IFilter,
            { title: "Address", member: "addressSearch", type: FilterType.text } as IFilter,
            { title: "Permit", member: "environmentalPermitSearch", type: FilterType.text } as IFilter,
            { title: "Facility code", member: "facilityOperationCodeSearch", type: FilterType.text } as IFilter,
            {
                title: "Weighbridge",
                member: "weighbridgeUsedFlag",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
            } as IFilter,
            {
                title: "Enabled",
                member: "isEnabled",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
            } as IFilter,
            {
                title: "Reporting active",
                member: "reportingActiveFlag",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
            } as IFilter,
            ...(isAdmin
                ? [
                      {
                          title: "Deleted",
                          member: "isDeleted",
                          type: FilterType.select,
                          options: { All: null, Yes: true, No: false },
                          defaultValue: false,
                      } as IFilter,
                  ]
                : []),
        ],
        [isAdmin]
    );

    const itemActions: IPagedListAction[] = useMemo(() => {
        if (!isAdmin) return [];
        return [
            {
                text: "Edit",
                onClick: (item: { id: string }) => navigate(`/sites/${item.id}`),
                primary: true,
            } as IPagedListAction,
            ...buildSiteAdminPagedListActions((kind, item) => openConfirm(kind, item)),
        ];
    }, [isAdmin, navigate]);

    const headActions: IPagedListAction[] = useMemo(() => {
        if (!isAdmin) return [];
        return [
            {
                text: addEntityActionLabel(EntityLabels.site),
                icon: faPlus,
                onClick: () => navigate("/sites/new"),
                primary: true,
            } as IPagedListAction,
        ];
    }, [isAdmin, navigate]);

    return (
        <Container fluid>
            <Helmet>
                <title>{ListPageTitles.sites}</title>
            </Helmet>
            <Row className="mb-3">
                <Col>
                    <ResponsiveBreadcrumb
                        items={[
                            { text: SectionLabels.operations, to: "/operations" },
                            { text: ListPageTitles.sites },
                        ]}
                    />
                    <h1>{ListPageTitles.sites}</h1>
                    <p className="text-muted">Regulated yards for your organisation. Organisation administrators can add, edit, and disable sites.</p>
                    {actionError && (
                        <Alert variant="danger" className="mt-2" onClose={() => setActionError(null)} dismissible>
                            {actionError}
                        </Alert>
                    )}
                </Col>
            </Row>
            <Row>
                <Col>
                    <PagedList
                        columns={columns}
                        filters={filters}
                        headActions={headActions}
                        itemActions={itemActions}
                        rowActionsAriaLabel="Site actions"
                        showDeletedRowIndicator
                        defaultSortBy="siteName"
                        defaultSortOrder={SortOrder.ascending}
                        getUrl="/api/sites/all"
                        rowFormatter={() => null}
                        persistanceKey="SitesListPage"
                        filterMigrations={sitesListFilterMigrations}
                        refreshNumber={refreshNumber}
                    />
                </Col>
            </Row>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runConfirmedAction();
                    else closeConfirm();
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />
        </Container>
    );
};

export { SitesListPage };
